import React, { useMemo, useState } from 'react';
import { TickMarkOutlined } from '@duxton-web/icons';
import { Table, Typography } from '@duxton-web/ui';
import { ColumnProps } from '@duxton-web/ui/lib/table';

import { AccessScopeSelect } from './access-scope-select';
import {
  ACCESS_CONTROL_PAGE_SIZE,
  AccessControlRow,
  buildAccessControlRows,
  buildAccessRequestRow,
  filterAccessControlRows,
  getAccessRequestUrl,
  shouldShowAccessRequestRow,
} from './utils';
import { useUserRolesContext } from '@/contexts/UserRolesContext';

const renderAccessMark = (hasAccess: boolean) =>
  hasAccess ? (
    <span aria-label="Checked" className="access-control-check">
      <TickMarkOutlined />
    </span>
  ) : (
    <span aria-label="No access" className="access-control-empty">
      -
    </span>
  );

const baseColumns: ColumnProps<AccessControlRow>[] = [
  {
    title: 'Service / Tech Family',
    dataIndex: 'serviceOrTechFamily',
    key: 'serviceOrTechFamily',
    render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
  },
];

const serviceAuthZColumnConfigs: ColumnProps<AccessControlRow>[] = [
  {
    title: 'Rollout Critical Publisher',
    dataIndex: 'rolloutCriticalPublisher',
    key: 'rolloutCriticalPublisher',
    align: 'center',
    render: renderAccessMark,
  },
  {
    title: 'Rollout Non-Critical Publisher',
    dataIndex: 'rolloutNonCriticalPublisher',
    key: 'rolloutNonCriticalPublisher',
    align: 'center',
    render: renderAccessMark,
  },
  {
    title: 'Experiment Publisher',
    dataIndex: 'experimentPublisher',
    key: 'experimentPublisher',
    align: 'center',
    render: renderAccessMark,
  },
];

const adminColumnConfig: ColumnProps<AccessControlRow> = {
  title: 'Admin',
  dataIndex: 'admin',
  key: 'admin',
  align: 'center',
  render: renderAccessMark,
};

const legacyColumnConfigs: ColumnProps<AccessControlRow>[] = [
  {
    title: 'Editor',
    dataIndex: 'editor',
    key: 'editor',
    align: 'center',
    render: renderAccessMark,
  },
  {
    title: 'Publisher',
    dataIndex: 'publisher',
    key: 'publisher',
    align: 'center',
    render: renderAccessMark,
  },
  adminColumnConfig,
];

export const AccessControlTable = () => {
  const { userTFRoles, userRoles, enableServiceAuthZ, isAdmin } =
    useUserRolesContext();
  const [selectedScope, setSelectedScope] = useState('');
  const accessRequestUrl = getAccessRequestUrl();

  const columns = useMemo(() => {
    const permissionColumnConfigs = [
      ...(enableServiceAuthZ ? serviceAuthZColumnConfigs : []),
      ...legacyColumnConfigs,
    ];

    const permissionColumns = permissionColumnConfigs.map((column, index) => ({
      ...column,
      onCell: (row: AccessControlRow) =>
        row.accessRequest
          ? { colSpan: index === 0 ? permissionColumnConfigs.length : 0 }
          : {},
      render: (value: boolean, row: AccessControlRow) => {
        if (!row.accessRequest) return renderAccessMark(value);

        if (index !== 0 || !accessRequestUrl) return null;

        return (
          <Typography.Link
            href={accessRequestUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here to apply
          </Typography.Link>
        );
      },
    }));

    return [...baseColumns, ...permissionColumns];
  }, [accessRequestUrl, enableServiceAuthZ]);

  const rows = useMemo(
    () => buildAccessControlRows(userRoles, userTFRoles, isAdmin),
    [isAdmin, userRoles, userTFRoles],
  );

  const filteredRows = useMemo(
    () => filterAccessControlRows(rows, isAdmin ? '' : selectedScope),
    [isAdmin, rows, selectedScope],
  );

  const showAccessRequestRow = shouldShowAccessRequestRow({
    selectedScope,
    filteredRows,
    isAdmin,
    enableServiceAuthZ,
  });

  const tableRows = useMemo(
    () =>
      showAccessRequestRow
        ? [buildAccessRequestRow(selectedScope)]
        : filteredRows,
    [filteredRows, selectedScope, showAccessRequestRow],
  );

  return (
    <div className="access-control-card-content">
      <div className="access-control-section-header">
        <AccessScopeSelect value={selectedScope} onChange={setSelectedScope} />
      </div>
      <Table
        size="middle"
        className="grab-x__table"
        columns={columns}
        dataSource={tableRows}
        pagination={{
          pageSize: ACCESS_CONTROL_PAGE_SIZE,
          showSizeChanger: false,
        }}
      />
    </div>
  );
};

export default AccessControlTable;
